import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress, StarRating } from '@/components/ui/Progress';
import { useGameStore } from '@/stores/gameStore';
import { useUserStore } from '@/stores/userStore';

interface Objective {
  id: string;
  description: string;
  required: boolean;
  tool: string;
  completed: boolean;
}

interface Hint {
  level: number;
  text: string;
  cost: number;
}

interface InstructionStep {
  step: number;
  title: string;
  description: string;
}

interface Instructions {
  title: string;
  steps: InstructionStep[];
  tips: string[];
}

interface NonTechnicalLevel {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  estimatedTime: number;
  objectives: Objective[];
  instructions: Instructions;
  hints: Hint[];
  tools: string[];
  completionMessage: string;
  xpReward: number;
  sampleDocument?: any;
  sampleSpreadsheet?: any;
  sampleImages?: any[];
  sampleDocuments?: any[];
}

interface NonTechnicalLevelContainerProps {
  levelData: NonTechnicalLevel;
}

export function NonTechnicalLevelContainer({ levelData }: NonTechnicalLevelContainerProps) {
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState(levelData.objectives);
  const [showHint, setShowHint] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const { hintsUsed, useHint: incrementHints, reset } = useGameStore();
  const { addXP, updateProgress, progress: userProgress } = useUserStore();

  const completedCount = objectives.filter((obj) => obj.completed).length;
  const totalCount = objectives.length;
  const progress = (completedCount / totalCount) * 100;

  // Reset state when level data changes
  useEffect(() => {
    setObjectives(levelData.objectives);
    setShowHint(false);
    setCurrentHintIndex(0);
    setIsCompleted(false);
  }, [levelData.id]);

  useEffect(() => {
    // Check if all required objectives are completed
    const allCompleted = objectives
      .filter((obj) => obj.required)
      .every((obj) => obj.completed);

    if (allCompleted && !isCompleted) {
      setIsCompleted(true);
      handleLevelComplete();
    }
  }, [objectives, isCompleted]);

  const handleLevelComplete = () => {
    // Calculate stars based on hints used
    let stars: 0 | 1 | 2 | 3 = 3;
    if (hintsUsed > 2) stars = 1;
    else if (hintsUsed > 0) stars = 2;

    // Check if level was already completed
    const previousProgress = userProgress.tutorialProgress.find(
      (p) => p.levelId === levelData.id
    );
    const wasAlreadyCompleted = previousProgress?.completed || false;

    console.log('🎯 Non-technical level completed:', levelData.id, {
      stars,
      xp: levelData.xpReward,
      wasAlreadyCompleted,
      awardingXP: !wasAlreadyCompleted
    });

    // Update user progress
    updateProgress(levelData.id, {
      completed: true,
      stars,
      completedAt: new Date(),
      attempts: 1,
    });

    // Award XP only if not previously completed
    if (!wasAlreadyCompleted) {
      addXP(levelData.xpReward);
    }
  };

  const handleHintClick = () => {
    if (currentHintIndex < levelData.hints.length) {
      setShowHint(true);
      incrementHints();
    }
  };

  const handleNextHint = () => {
    if (currentHintIndex < levelData.hints.length - 1) {
      setCurrentHintIndex(currentHintIndex + 1);
      incrementHints();
    }
  };

  const handleObjectiveToggle = (objectiveId: string) => {
    setObjectives((prev) =>
      prev.map((obj) =>
        obj.id === objectiveId ? { ...obj, completed: !obj.completed } : obj
      )
    );
  };

  const handleDownloadSample = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleNextLevel = () => {
    // Extract level number and go to next
    const isNonTech = levelData.id.startsWith('nt-');
    const currentLevelNum = parseInt(levelData.id.split('-').pop() || '1');
    const nextLevelNum = currentLevelNum + 1;
    const prefix = isNonTech ? 'nt-level-' : 'level-';

    // Reset game state for new level
    reset();

    // Navigate to next level
    navigate(`/tutorial/${prefix}${nextLevelNum}`);
  };

  const handleBackToLevels = () => {
    reset();
    navigate('/tutorial');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Level Header */}
          <Card variant="elevated">
            <CardHeader>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <Badge variant="info" size="sm" className="mb-2">
                    {levelData.difficulty}
                  </Badge>
                  <CardTitle className="text-2xl">{levelData.title}</CardTitle>
                </div>
                <div className="text-right">
                  <div className="text-primary-300 text-sm mb-1">
                    ~{levelData.estimatedTime} min
                  </div>
                  <StarRating stars={isCompleted ? 3 : 0} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-primary-200 mb-4">{levelData.description}</p>
              <Progress value={progress} showLabel />
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card variant="outline">
            <CardHeader>
              <CardTitle>{levelData.instructions.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {levelData.instructions.steps.map((step) => (
                  <div key={step.step} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 font-bold">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary-300 mb-1">
                        {step.title}
                      </h4>
                      <p className="text-sm text-primary-200/80">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tips */}
              {levelData.instructions.tips && levelData.instructions.tips.length > 0 && (
                <div className="mt-6 p-4 bg-dark-100/50 rounded-lg border border-primary-600/20">
                  <h4 className="font-semibold text-primary-400 mb-3">Tips</h4>
                  <div className="space-y-2">
                    {levelData.instructions.tips.map((tip, idx) => (
                      <p key={idx} className="text-sm text-primary-300">
                        {tip}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sample Documents */}
          {(levelData.sampleDocument || levelData.sampleSpreadsheet || levelData.sampleImages || levelData.sampleDocuments) && (
            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Sample Files</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {levelData.sampleDocument && (
                    <div className="p-3 bg-dark-100 rounded-lg border border-primary-600/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">📄</span>
                          <span className="font-semibold text-primary-300">
                            {levelData.sampleDocument.filename}
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            handleDownloadSample(
                              levelData.sampleDocument.filename,
                              levelData.sampleDocument.content
                            )
                          }
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  )}

                  {levelData.sampleSpreadsheet && (
                    <div className="p-3 bg-dark-100 rounded-lg border border-primary-600/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">📊</span>
                          <span className="font-semibold text-primary-300">
                            {levelData.sampleSpreadsheet.filename}
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            handleDownloadSample(
                              levelData.sampleSpreadsheet.filename,
                              levelData.sampleSpreadsheet.content
                            )
                          }
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  )}

                  {levelData.sampleImages && levelData.sampleImages.map((image, idx) => (
                    <div key={idx} className="p-3 bg-dark-100 rounded-lg border border-primary-600/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">🖼️</span>
                          <div>
                            <span className="font-semibold text-primary-300 block">
                              {image.filename}
                            </span>
                            <span className="text-xs text-primary-400">
                              {image.description}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            handleDownloadSample(image.filename, image.content)
                          }
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}

                  {levelData.sampleDocuments && levelData.sampleDocuments.map((doc, idx) => (
                    <div key={idx} className="p-3 bg-dark-100 rounded-lg border border-primary-600/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">📄</span>
                          <span className="font-semibold text-primary-300">
                            {doc.filename}
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            handleDownloadSample(doc.filename, doc.content)
                          }
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Objectives */}
          <Card variant="outline">
            <CardHeader>
              <CardTitle>Your Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-primary-300 mb-4">
                Check off each objective as you complete it:
              </p>
              <div className="space-y-3">
                {objectives.map((objective) => (
                  <div
                    key={objective.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      objective.completed
                        ? 'border-primary-600/50 bg-primary-950/30'
                        : 'border-dark-200 bg-dark-50 hover:border-primary-600/30'
                    }`}
                    onClick={() => handleObjectiveToggle(objective.id)}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {objective.completed ? (
                        <svg
                          className="w-5 h-5 text-primary-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-dark-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p
                        className={
                          objective.completed
                            ? 'text-primary-300 line-through'
                            : 'text-primary-200'
                        }
                      >
                        {objective.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="default" size="sm">
                          {objective.tool}
                        </Badge>
                        {objective.required && (
                          <span className="text-xs text-primary-400">
                            Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Completion Message */}
          {isCompleted && (
            <Card variant="elevated" className="border-primary-600/50">
              <CardContent>
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">🎉</div>
                  <h3 className="text-xl font-bold text-primary-400 mb-2">
                    Level Complete!
                  </h3>
                  <p className="text-primary-200 whitespace-pre-line mb-4">
                    {levelData.completionMessage}
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <Badge variant="success" size="lg">
                      +{levelData.xpReward} XP
                    </Badge>
                    <StarRating stars={3} size="lg" />
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleBackToLevels}
                    >
                      Back to Levels
                    </Button>
                    {parseInt(levelData.id.split('-').pop() || '1') < 5 && (
                      <Button variant="primary" size="lg" onClick={handleNextLevel}>
                        Next Level →
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Hints */}
          <Card>
            <CardHeader>
              <CardTitle>Hints</CardTitle>
            </CardHeader>
            <CardContent>
              {showHint ? (
                <div className="space-y-3">
                  <div className="p-3 bg-dark-100 rounded-lg border border-primary-600/30">
                    <p className="text-primary-200 text-sm">
                      {levelData.hints[currentHintIndex].text}
                    </p>
                  </div>
                  {currentHintIndex < levelData.hints.length - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleNextHint}
                    >
                      Show Next Hint (-
                      {levelData.hints[currentHintIndex + 1].cost} XP)
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-primary-200/70 text-sm mb-3">
                    Need help? Use a hint to get started.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={handleHintClick}
                  >
                    Show Hint (-{levelData.hints[0].cost} XP)
                  </Button>
                </div>
              )}
              <div className="mt-3 text-xs text-primary-300">
                Hints used: {hintsUsed}
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <Card>
            <CardContent className="text-sm text-primary-200/70">
              <p className="mb-2">
                💡 Click on objectives to mark them complete.
              </p>
              <p>⭐ Use fewer hints to earn more stars!</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
